import React, {useCallback, useEffect, useState} from "react";
import {DashboardWFRecord, Detail} from "@commons/mapping/dashboard.types";
import {DashboardWFParams, MonitorWFParams} from "@commons/services/presenze_api/dashboard.api";
import {range} from "@commons/utils/util";
import moment, {Moment} from "moment";
import classNames from "classnames";
import styles from "./wf.dashboard.module.css"
import TooltipEx from "@components/Tooltip";
import {Legenda} from "./comps/wf.dashboard.legenda";
import PeriodoRif from "@components/PeriodoRif";
import {useForm} from "react-hook-form";
import {useAppSelector} from "@hooks/redux-hooks/hooks";
import {selectAzienda, selectIdAzienda, selectIdAziendaMaster, selectPeriodo, selectUser} from "@commons/core.slice";
import {PeriodoForm, useHandlersPeriodoForm} from "@hooks/useHandlersPeriodoForm";
import Banner, {bannerStyle} from "@components/Banner";
import {OptionsElencoLavoratori} from "./comps/OptionsElencoLavoratori";
import {DayCell, DayParams} from "./comps/DayCell";
import Loading from "@components/dataTable/Loading";
import {usePagination} from "@components/dataTable/usePagination";
import Pagination from "@components/dataTable/Pagination";
import DashboardDetailWF from "./wf.dashboard.detail";
import Select from "@components/wrapperComponents/Select";
import {ModalPanelForm} from "@components/Modal2023";
import {useCalendarioChiusure, useDashboardWF, useMonitorWF, usePutSingolaRichiestaWF, useSingolaRichiestaWF} from "@commons/services/presenze_api/dashboard.queries";
import {PresenzeCausaleWFRecord} from "@commons/mapping/PresenzeCausaleWF";
import {UserCRUDRecord} from "@commons/mapping/UserCRUDRecord";
import {FPNetDate, IndiceMese, Periodo} from "@commons/types";
import {useValoriPolicies} from "@commons/services/presenze_api/tabelleAzienda.queries";
import {useTabellaWF} from "@commons/services/presenze_api/workflow.queries";
import {AziendaRecord} from "@commons/mapping/AziendaRecord";
import {toast} from "react-toastify";
import {TableRichiesteWF} from "./TableRichiesteWF";
import BannerWF from "./comps/BannerWF";
import {DateTime} from "luxon";
import {IconBan, IconCheck, IconHourglass, IconInfoCircleFilled, IconScale, IconTrash} from "@tabler/icons-react";
import {TabellaWFRecord} from "@commons/mapping/TabellaWFRecord";

const DayLabel: React.FC<{
    className?: string,
    children?: React.ReactNode
}> = ({className, children}) => {
    const commonClassNames = classNames(styles.cella, styles.cella_w, 'text-xs text-center border-r', className);
    return <div className={commonClassNames}>{children}</div>
}

//************************************************************************************************************************
//* DashboardWF **********************************************************************************************************

interface BadgeCausaleMancante {
    codice: string;
    descrizione: string;
    //bgColor: string; // nel format bg-[#1da1f2]
    bgColor: any; // {'backgroundColor': '#1da1f2'}
}

interface DashboardWFForm extends PeriodoForm {
    idAzElencoLavoratori: number,
    codTabella: string,
}

export const DashboardWF: React.FC = () => {
    const user: UserCRUDRecord | null = useAppSelector(selectUser); // User corrente
    const azienda = {idAzienda: useAppSelector(selectIdAzienda), idAziendaMaster: useAppSelector(selectIdAziendaMaster)}; // Azienda corrente
    const aziendaRecord: AziendaRecord | null = useAppSelector(selectAzienda);// Azienda corrente

    // Chiudo elenco giornaliero al variare dell'azienda
    useEffect(() => {
        chiudiElenco();
    }, [azienda.idAzienda, azienda.idAziendaMaster]);

    // Gestione selezione anno/mese
    const [periodo, setNextPeriodo] = useState<Periodo>(useAppSelector(selectPeriodo));
    const defaultValues: DashboardWFForm = {idAzElencoLavoratori: 0, anno: periodo.anno, mese: periodo.mese, codTabella: ''};
    const form = useForm<DashboardWFForm>({defaultValues});
    const {watch, register, resetField,} = form;

    const [deleteRichiesta, setNextDeleteRichiesta] = useState<boolean>(false);
    const [showDashboard, setNextShowDashboard] = useState<boolean>(true);
    const [mostraTutto, setMostraTutto] = useState<boolean>(false);
    // al cambiamento di anno o mese
    const onChange = useCallback((_anno: number, _mese: number) => {
        setNextShowDashboard(false);
    }, []);
    // alla conferma della selezione di anno o mese
    const onApply = useCallback((anno: number, mese: IndiceMese) => {
        setNextShowDashboard(true);
        setNextPeriodo({anno, mese});
        chiudiElenco();
    }, []);

    const {data: tabellaWF} = useTabellaWF(aziendaRecord ? {idDominio: aziendaRecord.idDominio!, idTabella: aziendaRecord.idTabellaWF} : null); // Carico tabella impianto WF
    const {elencoValoriTabella, elencoValoriCompleto} = useValoriPolicies(tabellaWF);
    const codTabella: string = watch('codTabella');

    const periodoRifOptions = {...useHandlersPeriodoForm(form, onChange, onApply), reload: () => refetch()};
    //const periodoRifOptions = {}
    const isEmployee = user?.userClass === 'EMPLOYEE';
    //const defaultPageLength = isEmployee ? 9999 : 20;
    const defaultPageLength = 20;
    const pagination = usePagination<string>('', 1, defaultPageLength);
    const params: DashboardWFParams = {
        idAziendaMaster: azienda?.idAziendaMaster,
        idAzienda: azienda?.idAzienda,
        anno: periodo.anno,
        mese: periodo.mese,
        start: pagination.getStart(),
        length: pagination.pageLength,
        tabellaWF: tabellaWF,
        codTabella,
    } as DashboardWFParams;
    const {refetch, isLoading, error, data} = useDashboardWF(params);
    const {isMonitorWFEditabile, getEditabileFinoA} = useCalendarioChiusure(aziendaRecord?.idAziendaCDIC || undefined, Periodo.getInizioMese(periodo), Periodo.getFineMese(periodo));
    const isWFEditabile = function () {
        console.log(Periodo.getDate(periodo).toISO());
        const isPeriodoChiuso = false;
        return !isPeriodoChiuso && isMonitorWFEditabile(Periodo.getDate(periodo).toJSDate(), Periodo.getDate(periodo).toJSDate()); // N.B. l'intervallo è significativo per anno/mese, nn serve indicare il giorno...
    };

    /**
     * Restituisce la dicitura che notifica la data entro la quale le richieste sono modificabili, es. ' - editabile fino al 03/07/2023'
     * Se il calendario non è presente, viene restituita una string vuota.
     */
    const getStringEditabileFinoA = function () {
        const data = getEditabileFinoA(currentDay, periodo);
        return data ? ` - editabile fino al ${data.toFormat('dd/LL/yyyy')}` : '';
    };

    //const [paramsTabella, setParamsTabella] = useState<TabellaAziendaParams | null>(null);
    const [badgesCausaliMancante, setNextBadgesCausaliMancante] = useState<BadgeCausaleMancante[]>([]);
    useEffect(() => {
        const nextBadges: BadgeCausaleMancante[] = [];
        //let params: typeof paramsTabella = null;
        if (data && data.data && data.data.length > 0)
            for (let lavoratore of data.data) {
                for (let detail of lavoratore.details) {
                    if (detail.tipoCausale === 'ORE_MANCANTI' && detail.coloreCausale) {
                        //const style = 'bg-[#f012be]';
                        if (nextBadges.findIndex((badge) => badge.codice === detail.codice) === -1)
                            //nextBadges.push({codice: detail.codice!, descrizione: detail.descrizioneCausale!, bgColor: `bg-[${detail.coloreCausale.toLowerCase()}]`});
                            nextBadges.push({codice: detail.codice!, descrizione: detail.descrizioneCausale!, bgColor: {'backgroundColor': detail.coloreCausale}});
                    }
                    /*if (detail.idTipoTabella || detail.idTipoTabella === 0) {
                        params = {idAziendaTabelle: detail.idAziendaTabelle!, idTabella: fromOrdinal2map(detail.idTipoTabella).url};
                    }*/
                }
            }
        setNextBadgesCausaliMancante(nextBadges);
        //setParamsTabella(params);
    }, [data]);


    const Badges: React.FC = () => {
        return <>{badgesCausaliMancante.map((badge) => {
            return <span key={badge.codice} className={`inline-flex items-center rounded-full px-2.5 py-0.5 mr-2 text-white font-medium`} style={badge.bgColor}>
                {badge.descrizione}
      </span>
        })
        }</>
    };


    const [currentDay, setNextCurrentDay] = useState<string | null>(null);
    const [monitorWFParams, setMonitorWFParams] = useState<MonitorWFParams>(() => {
        /*
            const p = {} as MonitorWFParams;
            if (data) {
                const totali = data.data[0].totali;
                p.dataDa = moment(totali[0].giornoDt).toDate();
                p.dataA = moment(totali[totali.length - 1].giornoDt).toDate();
            }
            return p;
        */
        return {idDip: null} as MonitorWFParams;
    });

    const monitorWF = useMonitorWF(monitorWFParams);

    const onDayClick = (p: DayParams) => {
        setMonitorWFParams((params) => {
            const details = data!.data[0].details;
            //const totali = p.totali;
            const dataDa = moment(details[0].giornoDt).toDate();
            const dataA = moment(details[details.length - 1].giornoDt).toDate();
            return {...params, dataDa, dataA, idAziendaMaster: p.idAziendaMaster, idAzienda: p.idAzienda, idDip: p.id};
        });
        setNextCurrentDay(p.details[0].giornoDt);
    }

    const chiudiElenco = () => {
        setMonitorWFParams((p) => {
            return {...p, idDip: null}
        });
        setNextURI(null);
    }

    const [URI, setNextURI] = useState<string | null>(null);
    const singolaRichiesta = useSingolaRichiestaWF(URI);
    const mutation = usePutSingolaRichiestaWF(URI, monitorWFParams);
    const loadResource = (uri: string) => {
        setNextDeleteRichiesta(false);
        setNextURI(uri);
    }
    const saveResource = (causale: PresenzeCausaleWFRecord) => {
        mutation.mutateAsync(causale).then(() => {
            toast('Richiesta aggiornata!', {type: 'success'});
        });
    }
    const deleteResource = (uri: string) => {
        setNextDeleteRichiesta(true);
        setNextURI(uri);
    }
    const onCancelEdit = () => {
        setNextURI(null);
    }

    const formatDate = (date: string) => moment(date).startOf('day').format('dddd LL');

    const idAzElencoLavoratori: number = watch('idAzElencoLavoratori');

    // Istanzio secondo hook form per gestione editing dettaglio richiesta
    const hookForm = useForm<PresenzeCausaleWFRecord>({mode: 'onChange'});
    const {reset: resetAsyncForm, control, setValue} = hookForm;
    useEffect(() => {
        // Come da documentazione https://react-hook-form.com/docs/useform/reset
        // values object An optional object to reset form values, and it's recommended to provide the entire defaultValues when supplied.
        resetAsyncForm({...PresenzeCausaleWFRecord.factory(), ...singolaRichiesta.data});
        if (deleteRichiesta) {
            setValue('wf.annullata', true);
        }
        console.log('resetAsyncForm!');
    }, [deleteRichiesta, setValue, resetAsyncForm, singolaRichiesta.data])

    if (error) return <>'An error has occurred: ' + {error.message};</>


    type PropsGiornata = { hhPreviste: number, hhWeekMedie: number, hhMancanti: number };
    /**
     Routine che determina il numero di hhPreviste, hhWeekMedie e hhMancanti
     hhMancanti rappresenta il totale mancante giustificato + quello relativo alle richieste di WF a ECCEZIONE di quella in edit (richiestaInEdit)!
     * @param lavoratore
     * @param richiestaInEdit
     */
    const getPropsGiornata = (lavoratore: DashboardWFRecord, richiestaInEdit: PresenzeCausaleWFRecord): PropsGiornata => {
        const {giornoDt, wf} = richiestaInEdit;
        const elencoRichiesteGiornata = lavoratore.details.filter((richiesta) => richiesta.giornoDt === giornoDt);
        if (elencoRichiesteGiornata.length) {
            let hhMancanti = 0;
            let primaVolta = true;
            for (const richiesta of elencoRichiesteGiornata) {
                if (primaVolta) { // Raccolgo totale giustificativi su mancante caricati in presenze (non da WF)
                    // N.B. è necessario verificare se ho già raccolto il mancante da presenze perché il record è presente tante volte quante sono le richieste di WF
                    // Comportamento da store procedure BROWSE_WORKFLOW_DASHBOARD2
                    if (richiesta.tipoOreGiustificativo === 'M') {
                        primaVolta = false;
                        hhMancanti += richiesta.quantitaGiustificativo;
                    }
                }
                if (richiesta.wf) {
                    if (richiesta.wf.idWF !== wf.idWF // N.B. escludo la richiesta in edit!!!
                        && !richiesta.wf.annullata // la richiesta nn deve essere annullata
                        && richiesta.wf.stato !== 'RIFIUTATA' // la richiesta nn deve essere stata rifiutata
                        && richiesta.tipoCausale === 'ORE_MANCANTI') // la richiesta deve riguardare le ore mancanti
                    {
                        hhMancanti += richiesta.quantita!;
                    }
                }
            }

            return {
                hhPreviste: elencoRichiesteGiornata[0].hhPreviste, // In minuti!
                hhWeekMedie: elencoRichiesteGiornata[0].hhWeekMedie, // In ore
                hhMancanti: (hhMancanti * 60),
            };
        }
        return {hhPreviste: 0, hhWeekMedie: 0, hhMancanti: 0};
    }


    // Metodo che verifica se per almeno un giorno il lavoratore appartiene al centro di costo o gruppo indicato
    /*const hasADay = (days: Detail[], codTabella: string) => {
        for (const day of days) {
            if (day.codTabella === codTabella)
                return true;
        }
        return false;
    }*/

    const legenda = <TooltipEx tooltip={<Legenda/>} placement="bottom">
        <IconInfoCircleFilled className="h-6 w-6 text-blue-500"/>
    </TooltipEx>;

// Guida su Grid Layouts
//https://www.youtube.com/watch?v=JITwwrVSteE
    const giorniPeriodo = (data && data.data.length > 0) ? data.data[0] : {} as DashboardWFRecord;
    return <>
        <div className="mb-4">
            <PeriodoRif nameAnno="anno" nameComboMese="mese" className="w-2/3"
                        disabled={showDashboard}
                        {...periodoRifOptions}/>
            <div className="mt-2 flex flex-row w-2/3">
                {(showDashboard && isEmployee) && <div className="w-1/2">
                    <Select label="Elenco lavoratori" id="idAzElencoLavoratori" name="idAzElencoLavoratori"
                            divClass="grow px-2" register={register} registerOptions={{valueAsNumber: true}}>
                        {<OptionsElencoLavoratori data={data}/>}
                    </Select>
                </div>}
                {(showDashboard && elencoValoriCompleto && elencoValoriTabella) && <div className="w-1/2">
                    <Select label={tabellaWF?.descrizioneTipo || "Raggruppamento"} id="codTabella" name="codTabella"
                            checked={mostraTutto}
                            onToggle={TabellaWFRecord.showOptions(tabellaWF) ? () => setMostraTutto(v => !v) : undefined}
                            onClear={() => resetField('codTabella')}
                            onChange={() => resetField('idAzElencoLavoratori')}
                            divClass="grow px-2" register={register}>
                        <>
                            <option value={""} className="text-gray-900 relative cursor-default select-none py-2 pl-3 pr-9">
                                Tutti
                            </option>
                            {(mostraTutto ? elencoValoriCompleto : elencoValoriTabella).map(item => (
                                <option key={item.id} value={item.id!} className="text-gray-900 relative cursor-default select-none py-2 pl-3 pr-9">
                                    {item.descrizione}
                                </option>))}
                        </>
                    </Select>
                </div>}
            </div>
        </div>

        {!showDashboard && <Banner style={bannerStyle.warning} text="Seleziona un periodo e/o premi il bottone Applica!"/>}

        {showDashboard && <Loading loading={isLoading}>
            {(data && data.data.length === 0) && <Banner style={bannerStyle.danger} text="Per l'azienda e/o il periodo selezionato non è presente alcun lavoratore."/>}
            {(data && data.data.length > 0) && <>
                <div className="flex flex-row gap-2 justify-end mb-2 text-white text-lg font-semibold">
                    <TooltipEx tooltip={`Richieste in attesa di valutazione`}>
                        <div className={classNames(styles.stateContainer, styles.inAttesaBkColor)}>
                            <IconHourglass size={16}/>
                            {data.details.count0}
                        </div>
                    </TooltipEx>
                    <TooltipEx tooltip={`Richieste in valutazione`}>
                        <div className={classNames(styles.stateContainer, styles.inValutazioneBkColor)}><IconScale size={16}/>{data.details.count1}</div>
                    </TooltipEx>
                    <TooltipEx tooltip={`Richieste accettate`}>
                        <div className={classNames(styles.stateContainer, styles.accettataBkColor)}><IconCheck className="h-5 w-5" aria-hidden="true"/>{data.details.count2}</div>
                    </TooltipEx>
                    <TooltipEx tooltip={`Richieste respinte`}>
                        <div className={classNames(styles.stateContainer, styles.respintaBkColor)}>
                            <IconBan size={16}/>
                            {data.details.count3}</div>
                    </TooltipEx>
                    <TooltipEx tooltip={`Richieste annullate`}>
                        <div className={classNames(styles.stateContainer, styles.annullataBkColor)}>
                            <IconTrash className="h-5 w-5" aria-hidden="true"/>
                            {data.details.countAnnullate}
                        </div>
                    </TooltipEx>
                </div>
                <div className="overflow-hidden overflow-x-auto bg-gray-50 pt-2 pb-2 shadow ring-1 ring-black ring-opacity-5 rounded-t-lg">
                    {/* ****************************** riga etichette settimane ****************************** */}
                    <div className="px-2 grid grid-cols-4">
                        <div className={classNames('', styles.etichettaRiga)}></div>
                        <div className="col-span-3 inline-flex">
                            <SviluppaRiga giorniPeriodo={giorniPeriodo} cellRender={
                                (giorno, _causali) => {
                                    return <DayLabel key={giorno.toDate().getTime()}>
                                        {giorno.day() === 1 ? giorno.isoWeek() : ''}{/* 0=> domenica, 1=>lunedì*/}
                                    </DayLabel>
                                }
                            }></SviluppaRiga>
                        </div>
                    </div>
                    {/* ****************************** riga etichette giorni periodo ****************************** */}
                    <div className="border-b-2 px-2 grid grid-cols-4">
                        <>
                            <div className={classNames('font-semibold text-gray-900', styles.etichettaRiga)}>Lavoratori</div>
                            <div className="col-span-3 inline-flex">
                                <SviluppaRiga giorniPeriodo={giorniPeriodo} cellRender={
                                    (giorno, causali) => {
                                        const {weekend} = Detail.isWeekend(giorno.toDate());
                                        const festivo = causali.length ? causali[0].festivo : false;
                                        const oddWeek = !(giorno.isoWeek() % 2 === 0);
                                        return <DayLabel key={giorno.toDate().getTime()}
                                                         className={classNames((weekend || festivo) && 'text-white bg-red-400', (!weekend && oddWeek) && 'bg-blue-100')}>
                                            {giorno.format('DD')}
                                        </DayLabel>
                                    }
                                }></SviluppaRiga>
                            </div>
                        </>
                    </div>
                    {/* ****************************** righe lavoratori ****************************** */}
                    {
                        data.data
                            .filter(item => item.idAziendaMaster === idAzElencoLavoratori || idAzElencoLavoratori === 0)
                            //.filter(item => codTabella === '' || hasADay(item.details, codTabella))
                            .map((lavoratore: DashboardWFRecord, index: number) => {
                                const altezzaRiga = (lavoratore.id !== monitorWFParams.idDip) ? styles.altezzaRiga : undefined;
                                let props: PropsGiornata | undefined = undefined;
                                if (singolaRichiesta.data && singolaRichiesta.data.id === lavoratore.id) {
                                    props = getPropsGiornata(lavoratore, singolaRichiesta.data);
                                }
                                console.log("props", props)
                                return (
                                    <div key={lavoratore.id} className={classNames('px-2 grid grid-cols-4', (index % 2 === 0) ? 'bg-white' : 'bg-gray-100')}>
                                        <div className={classNames('self-center', styles.etichettaRiga)}>
                                        <span className={classNames(user!.codiceFiscale === lavoratore.codiceFiscale && 'font-semibold text-red-500')}>
                                        {lavoratore.cognome}&nbsp;{lavoratore.nome}
                                            </span>
                                        </div>
                                        <div className="self-center col-span-3">
                                            <div className="max-w-fit">
                                                <div className="inline-flex">
                                                    <SviluppaRiga className={altezzaRiga} giorniPeriodo={lavoratore} cellRender={
                                                        (giorno, causali) => {
                                                            const isSelected = moment(currentDay).startOf('day').isSame(giorno)
                                                            return <DayCell key={giorno.toDate().getTime()} onClick={onDayClick}
                                                                            idAziendaMaster={lavoratore.idAziendaMaster}
                                                                            idAzienda={lavoratore.idAzienda}
                                                                            idDip={lavoratore.id}
                                                                            details={causali}
                                                                            isSelected={isSelected}
                                                                            monitorWFParams={monitorWFParams}/>
                                                        }
                                                    }></SviluppaRiga>
                                                </div>

                                                {lavoratore.id === monitorWFParams.idDip &&
                                                    <>
                                                        <div className="p-1 border-l-2 border-r-2 border-b-2 border-gray-500 mb-1">
                                                            <div>{formatDate(currentDay!)}{getStringEditabileFinoA()}</div>
                                                            <TableRichiesteWF data={monitorWF.data} loadResource={loadResource} deleteResource={deleteResource} URI={URI}
                                                                              currentDay={currentDay}
                                                                              isEditabile={(_rifDate: FPNetDate) => isMonitorWFEditabile(Periodo.getDate(periodo).toJSDate(), Periodo.getDate(periodo).toJSDate(), DateTime.fromISO(currentDay!).toJSDate())}
                                                                              page="dashboard"/>
                                                            <div className="m-3 flex flex-row justify-end">
                                                                <button
                                                                    className="btnDefault"
                                                                    onClick={() => chiudiElenco()}> Chiudi
                                                                </button>
                                                            </div>
                                                            {singolaRichiesta.data &&
                                                                <ModalPanelForm isOpen={!!singolaRichiesta.data} formControl={control} handleDismiss={onCancelEdit}
                                                                                header={<BannerWF richiesta={singolaRichiesta.data} lavoratore={lavoratore}/>}>
                                                                    <DashboardDetailWF
                                                                        hook={hookForm}
                                                                        richiesta={singolaRichiesta.data} periodo={periodo}
                                                                        deleteRichiesta={deleteRichiesta}
                                                                        hhPreviste={props!.hhPreviste}
                                                                        hhWeekMedie={props!.hhWeekMedie}
                                                                        hhMancanti={props!.hhMancanti}
                                                                        onCancel={onCancelEdit}
                                                                        onSave={saveResource}
                                                                        page="dashboard"
                                                                        lavoratore={lavoratore}
                                                                        isEditabile={isWFEditabile()}
                                                                    />
                                                                </ModalPanelForm>
                                                            }
                                                        </div>
                                                    </>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                )
                            })
                    }
                </div>

                {data.recordsFiltered <= defaultPageLength &&
                    <div className="bg-white px-4 py-3 border-t border-t-gray-200 shadow ring-1 ring-black ring-opacity-5 rounded-b-lg flex flex-row items-center gap-1">
                        <div className="h-6 w-6">
                            {legenda}
                        </div>
                        <Badges/>
                    </div>}

                {data.recordsFiltered > defaultPageLength && <><Pagination dataTables={data} currentPage={pagination.page} setPage={pagination.setPage}
                                                                           currentPageLength={pagination.pageLength}
                                                                           auxComp={<span className="pr-2">{legenda}</span>}
                ></Pagination>
                    {badgesCausaliMancante.length > 0 && <div className="mt-2"><Badges/></div>}
                </>}
            </>}
        </Loading>}
    </>;
}

/**
 * Componente di aiuto per lo sviluppo di una singola riga composta da un massimo sei div (rappresentanti le sei settimane che compongono il periodo selezionato);
 * all'interno di ciascun div, vengono implementate le sette celle (rappresentanti i sette giorni della settimana) attraverso la chiamata a una funzione di render specifica.
 * @param giorniPeriodo istanza di DashboardWFRecord, rappresenta il lavoratore e il relativo dettaglio di presenze/assenze
 * @param cellRender funzione di render della cella
 * @param className ulteriore style della riga (es. altezza)
 * @constructor
 */
const SviluppaRiga: React.FC<{
    giorniPeriodo: DashboardWFRecord,
    cellRender: (giorno: Moment, causali: Detail[]) => React.ReactNode,
    className?: string
}> = ({
          giorniPeriodo,
          cellRender,
          className
      }) => {
    const giornoStart = moment(giorniPeriodo.details[0].giornoDt).startOf('day');
    const cercaGiorno = (giorno: Moment) => (detail: Detail) => moment(detail.giornoDt).startOf('day').isSame(giorno); // cerca il giorno in valutazione all'interno dello array delle giornate/causali
    return <>{range(0, 6).map((indexWeek: number) => {
        const giorno = moment(giornoStart).add(7 * indexWeek, 'day'); // Lunedì della settimana in esame
        const causaliGiorno = giorniPeriodo.details.filter(cercaGiorno(giorno)); // Se la giornata è valorizzata proseguo sviluppando l'intera settimana, altrimenti nulla!
        if (causaliGiorno.length > 0)
            return <div key={indexWeek} className={classNames(className, "inline-flex border-r border-gray-600")}>
                {range(0, 7).map((index: number) => {
                    const giorno = moment(giornoStart).add((7 * indexWeek) + index, 'day');
                    const causaliGiorno = giorniPeriodo.details.filter(cercaGiorno(giorno));
                    // console.log(causaliGiorno);
                    return cellRender(giorno, causaliGiorno);
                })}
            </div>
        else return null;
    })}
    </>
}

export default DashboardWF;